// src/features/quotation/components/QuotationManager.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  Paper,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useQuotation } from "../hooks/useQuotation";
import QuotationStats from "./QuotationStats";
import QuotationList from "./QuotationList";
import QuotationForm from "./QuotationForm";
import QuotationPreview from "./QuotationPreview";
import QuotationFilter from "./QuotationFilter";

const QuotationManager = () => {
  const {
    quotations,
    loading,
    error,
    success,
    filters,
    stats,
    getQuotations,
    createNewQuotation,
    updateExistingQuotation,
    deleteExistingQuotation,
    updateStatus,
    sendQuotationToClient,
    convertToInvoiceQuotation,
    updateFilters,
    clearAllFilters,
    clearErrorMessage,
    clearSuccessMessage,
  } = useQuotation();

  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Auto-refresh quotations when filters change
  useEffect(() => {
    getQuotations();
  }, [filters]);

  // Handle snackbar
  useEffect(() => {
    if (error || success) {
      setSnackbarOpen(true);
    }
  }, [error, success]);

  const handleCreateQuotation = async (data) => {
    try {
      await createNewQuotation(data);
      setShowForm(false);
      getQuotations();
    } catch (err) {
      console.error("Create failed:", err);
    }
  };

  const handleUpdateQuotation = async (data) => {
    try {
      await updateExistingQuotation(editingQuotation.id, data);
      setShowForm(false);
      setEditingQuotation(null);
      getQuotations();
    } catch (err) {
      console.error("Update failed:", err);
    }
  };

  const handleDeleteQuotation = async (id) => {
    if (window.confirm("Delete this quotation?")) {
      try {
        await deleteExistingQuotation(id);
        getQuotations();
      } catch (err) {
        console.error("Delete failed:", err);
      }
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateStatus(id, status);
      getQuotations();
    } catch (err) {
      console.error("Status update failed:", err);
    }
  };

  const handleSendQuotation = async (id) => {
    try {
      await sendQuotationToClient(id);
      getQuotations();
    } catch (err) {
      console.error("Send failed:", err);
    }
  };

  const handleConvertToInvoice = async (id) => {
    if (window.confirm("Convert this quotation to invoice?")) {
      try {
        await convertToInvoiceQuotation(id);
        getQuotations();
      } catch (err) {
        console.error("Conversion failed:", err);
      }
    }
  };

  const handlePreview = (quotation) => {
    setSelectedQuotation(quotation);
    setShowPreview(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    clearErrorMessage();
    clearSuccessMessage();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1" fontWeight="bold">
          Quotation Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditingQuotation(null);
            setShowForm(true);
          }}
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          New Quotation
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <QuotationFilter
          filters={filters}
          onFilterChange={updateFilters}
          onClear={clearAllFilters}
        />
      </Paper>

      {/* List */}
      <QuotationList
        quotations={quotations}
        loading={loading}
        onEdit={(quotation) => {
          setEditingQuotation(quotation);
          setShowForm(true);
        }}
        onDelete={handleDeleteQuotation}
        onStatusChange={handleStatusChange}
        onSend={handleSendQuotation}
        onPreview={handlePreview}
        onConvert={handleConvertToInvoice}
      />

      {/* Create/Edit Dialog */}
      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingQuotation(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editingQuotation ? "Edit Quotation" : "Create New Quotation"}
        </DialogTitle>
        <DialogContent>
          <QuotationForm
            initialData={editingQuotation}
            onSubmit={
              editingQuotation ? handleUpdateQuotation : handleCreateQuotation
            }
            onCancel={() => {
              setShowForm(false);
              setEditingQuotation(null);
            }}
            loading={loading}
          />
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <QuotationPreview
        open={showPreview}
        onClose={() => setShowPreview(false)}
        quotation={selectedQuotation}
        onConvert={handleConvertToInvoice}
        onSend={handleSendQuotation}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? "error" : "success"}
          variant="filled"
        >
          {error || success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuotationManager;
